import React, { useState, useEffect } from 'react';
import { useCart } from '../../context/CartContext';
import styles from './CheckoutPage.module.scss';
import { useNavigate, Link } from 'react-router-dom';
import orderService from '../../services/orderService';
import { toast } from 'react-toastify';
import axios from 'axios'; 
import qrCodeImage from '../../assets/images/qr-code.jpg'; 
import { 
  IoCloudUploadOutline, IoCheckmarkCircle, IoPersonOutline, 
  IoCallOutline, IoMailOutline, IoLocationOutline, IoWalletOutline, 
  IoArrowBack, IoTicketOutline, IoCopyOutline, IoCloseOutline, 
  IoWarningOutline, IoHome, IoBan, IoDocumentTextOutline
} from 'react-icons/io5';

// 1. IMPORT HOOK LOADING
import { useLoading } from '../../context/LoadingContext/LoadingContext';

const PLACEHOLDER_IMAGE = '/images/placeholder.jpg';
const BACKEND_URL = 'https://huongtramcomoc.vn'; 

const generateOrderCode = () => {
  const randomNum = Math.floor(100000 + Math.random() * 900000);
  return `DH${randomNum}`;
};

// Danh sách ID Quận nội thành Hà Nội (Cần đảm bảo ID khớp với API esgoo.net trả về)
// Lưu ý: ID của esgoo thường là chuỗi số (ví dụ Ba Đình là "001", Hoàn Kiếm là "002"...)
// Dưới đây là bộ ID thông dụng của HN, bạn có thể log ra console để check lại ID chính xác từ API
const HANOI_INNER_DISTRICTS = [
  "001", "002", "003", "004", "005", "006", "007", "008", "009", "019", "020", "021" 
  // Ba Đình, Hoàn Kiếm, Tây Hồ, Long Biên, Cầu Giấy, Đống Đa, Hai Bà Trưng, Hoàng Mai, Thanh Xuân, Nam Từ Liêm, Bắc Từ Liêm, Hà Đông
];

const CheckoutPage = () => {
  const { cartItems, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [createdOrderCode, setCreatedOrderCode] = useState('');
  
  // --- STATE ĐỊA CHÍNH (3 CẤP) ---
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]); 
  const [wards, setWards] = useState([]); // Thêm Phường/Xã
  
  const [selectedProvinceId, setSelectedProvinceId] = useState('');
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [selectedWardId, setSelectedWardId] = useState(''); // Thêm ID Phường/Xã

  // --- STATE TÀI CHÍNH ---
  const [shippingFee, setShippingFee] = useState(0);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [hasDiscountedItems, setHasDiscountedItems] = useState(false);

  // --- FORM DATA ---
  // Thêm wardName vào addressParts
  const [addressParts, setAddressParts] = useState({ provinceName: '', districtName: '', wardName: '', specific: '' });
  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '', address: '', notes: '' });
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [proofFile, setProofFile] = useState(null);

  // --- MODAL ---
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [tempOrderData, setTempOrderData] = useState(null);

  // 1. INIT & CHECK DISCOUNT ITEM
  useEffect(() => {
    window.scrollTo(0, 0);
    if (cartItems.length === 0 && !showSuccessPopup) {
       navigate('/gio-hang');
    }
    
    const checkDiscount = cartItems.some(item => {
        const originalPrice = parseFloat(item.price || 0);
        const salePrice = parseFloat(item.sale_price || 0);
        return salePrice > 0 && salePrice < originalPrice;
    });

    setHasDiscountedItems(checkDiscount);
    if (checkDiscount) {
        setAppliedCoupon(null);
        setDiscountAmount(0);
    }

    // Lấy danh sách Tỉnh (Level 1)
    const fetchProvinces = async () => {
      try {
        const res = await axios.get('https://esgoo.net/api-tinhthanh/1/0.htm');
        if (res.data.error === 0) setProvinces(res.data.data);
      } catch (err) {
        console.error("Lỗi lấy tỉnh thành:", err);
      } 
    };
    fetchProvinces();
      
  }, [cartItems, navigate, showSuccessPopup]); 

  // 2. LOGIC TÍNH SHIP (CẬP NHẬT)
  const calculateShipping = (provId, distId) => {
    if (!provId) return 0;

    // ID của Hà Nội trong esgoo.net thường là "01"
    // Kiểm tra cả tên phòng trường hợp ID thay đổi
    const isHanoi = provId === '01' || addressParts.provinceName.includes('Hà Nội');

    if (isHanoi) { 
        if (!distId) return 0; // Chưa chọn huyện thì chưa tính
        // Check quận nội thành
        if (HANOI_INNER_DISTRICTS.includes(distId)) {
            return 30000; // Nội thành
        } else {
            return 50000; // Ngoại thành HN
        }
    }
    return 70000; // Tỉnh ngoài
  };

  useEffect(() => {
    const fee = calculateShipping(selectedProvinceId, selectedDistrictId);
    setShippingFee(fee);

    if (appliedCoupon && !hasDiscountedItems) {
       const totalCart = getCartTotal();
       let amount = 0;
       if (appliedCoupon.type === 'percent') {
          amount = totalCart * (appliedCoupon.value / 100);
       } else if (appliedCoupon.type === 'fixed') {
          amount = appliedCoupon.value;
       }
       if (amount > totalCart + fee) amount = totalCart + fee;
       setDiscountAmount(amount);
    } else {
       setDiscountAmount(0);
    }
  }, [selectedProvinceId, selectedDistrictId, appliedCoupon, cartItems, getCartTotal, hasDiscountedItems, addressParts.provinceName]);

  // 3. HANDLERS ĐỊA CHÍNH (3 CẤP)
  
  // -- Chọn Tỉnh --
  const handleProvinceChange = (e) => {
    const provinceId = e.target.value; 
    const index = e.target.selectedIndex;
    
    setSelectedProvinceId(provinceId);
    setAddressParts(prev => ({ 
      ...prev, 
      provinceName: index > 0 ? e.target.options[index].text : '', 
      districtName: '',
      wardName: '' // Reset Phường
    }));
    
    // Reset cấp dưới
    setDistricts([]); 
    setSelectedDistrictId('');
    setWards([]);
    setSelectedWardId('');
    
    if (provinceId) {
      showLoading(); 
      axios.get(`https://esgoo.net/api-tinhthanh/2/${provinceId}.htm`)
        .then(res => { if (res.data.error === 0) setDistricts(res.data.data); })
        .finally(() => hideLoading());
    }
  };

  // -- Chọn Quận/Huyện --
  const handleDistrictChange = (e) => {
    const districtId = e.target.value;
    const index = e.target.selectedIndex;
    
    setSelectedDistrictId(districtId);
    setAddressParts(prev => ({
        ...prev,
        districtName: index > 0 ? e.target.options[index].text : '',
        wardName: '' // Reset Phường
    }));

    // Reset cấp dưới
    setWards([]);
    setSelectedWardId('');

    if (districtId) {
        showLoading();
        // Gọi API lấy Phường/Xã (Level 3)
        axios.get(`https://esgoo.net/api-tinhthanh/3/${districtId}.htm`)
          .then(res => { if (res.data.error === 0) setWards(res.data.data); })
          .finally(() => hideLoading());
    }
  };

  // -- Chọn Phường/Xã (Mới) --
  const handleWardChange = (e) => {
      const wardId = e.target.value;
      const index = e.target.selectedIndex;

      setSelectedWardId(wardId);
      setAddressParts(prev => ({
          ...prev,
          wardName: index > 0 ? e.target.options[index].text : ''
      }));
  };

  const handleSpecificAddress = (e) => setAddressParts(prev => ({ ...prev, specific: e.target.value }));

  // Cập nhật chuỗi địa chỉ đầy đủ khi các thành phần thay đổi
  useEffect(() => {
    const { specific, wardName, districtName, provinceName } = addressParts;
    // Format: Số nhà, Phường, Quận, Tỉnh
    if (provinceName) {
        let fullAddr = '';
        if (specific) fullAddr += specific + ', ';
        if (wardName) fullAddr += wardName + ', ';
        if (districtName) fullAddr += districtName + ', ';
        fullAddr += provinceName;

        setFormData(prev => ({ ...prev, address: fullAddr }));
    }
  }, [addressParts]);

  const handleInputChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleFileChange = (e) => { if (e.target.files?.[0]) setProofFile(e.target.files[0]); };
  
  // 4. COUPON LOGIC
  const handleApplyCoupon = async () => {
    if (hasDiscountedItems) {
        toast.warning("Đơn hàng có sản phẩm khuyến mãi, không thể áp dụng thêm Voucher.");
        return;
    }
    const code = discountCode.trim().toUpperCase();
    if (!code) { toast.warning("Vui lòng nhập mã giảm giá!"); return; }
    
    showLoading(); 
    try {
      const res = await axios.get(`${BACKEND_URL}/api/endpoints/check_coupon.php?code=${code}`);
      if (res.data.success) {
        setAppliedCoupon(res.data.coupon);
        toast.success(`Áp dụng mã ${code} thành công!`);
        setDiscountCode('');
      }
    } catch (error) {
      setAppliedCoupon(null); setDiscountAmount(0);
      toast.error(error.response?.data?.message || "Mã giảm giá không hợp lệ.");
    } finally { 
      hideLoading(); 
    }
  };

  const removeCoupon = () => { setAppliedCoupon(null); setDiscountAmount(0); toast.info("Đã gỡ mã giảm giá."); };

  // 5. SUBMIT ORDER
  const cartTotal = getCartTotal();
  const finalTotal = Math.max(0, cartTotal + shippingFee - discountAmount);
  const formatPrice = (p) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(p);

  const handlePlaceOrderClick = (e) => {
    e.preventDefault();
    // Validate đủ 3 cấp
    if (!selectedProvinceId || !selectedDistrictId || !selectedWardId || !addressParts.specific) {
      toast.error('Vui lòng chọn đầy đủ Tỉnh, Quận, Phường và nhập địa chỉ chi tiết.'); 
      return;
    }

    const orderCode = generateOrderCode();
    const removeAccents = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D");
    const cleanName = removeAccents(formData.fullName).toUpperCase();
    const transferContent = `${cleanName} ${formData.phone} ${orderCode}`.trim();

    const tempOrder = {
      orderCode, transferContent, finalTotal, formData, items: cartItems, shippingFee, discountAmount, paymentMethod
    };

    setTempOrderData(tempOrder);
    sessionStorage.setItem('temp_order_data', JSON.stringify(tempOrder));

    if (paymentMethod === 'bank') { setShowPaymentModal(true); } 
    else { submitOrderToBackend(tempOrder); }
  };

  const submitOrderToBackend = async (orderData) => {
    if (paymentMethod === 'bank' && !proofFile) {
      toast.error('Vui lòng tải ảnh minh chứng chuyển khoản!'); return;
    }

    showLoading(); 
    
    const apiFormData = new FormData();
    apiFormData.append('orderId', orderData.orderCode);
    apiFormData.append('customerInfo', JSON.stringify(orderData.formData));
    apiFormData.append('items', JSON.stringify(orderData.items));
    apiFormData.append('total', orderData.finalTotal);
    apiFormData.append('shipping_fee', orderData.shippingFee);
    apiFormData.append('discount_amount', orderData.discountAmount);
    apiFormData.append('paymentMethod', orderData.paymentMethod);
    if(appliedCoupon) apiFormData.append('coupon_code', appliedCoupon.code);
    if (proofFile) apiFormData.append('payment_proof_file', proofFile);

    try {
      await orderService.createOrder(apiFormData);
      sessionStorage.removeItem('temp_order_data');
      setShowPaymentModal(false);
      setCreatedOrderCode(orderData.orderCode);
      setShowSuccessPopup(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Lỗi đặt hàng. Vui lòng thử lại.');
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessPopup = () => {
      clearCart(); 
      setShowSuccessPopup(false);
      navigate('/');
  };

  const handleCopyContent = () => {
    if(tempOrderData) {
      navigator.clipboard.writeText(tempOrderData.transferContent);
      toast.success("Đã sao chép nội dung!");
    }
  };

  return (
    <div className={styles.pageWrapper}>
      
      {/* STEPPER */}
      <div className={styles.stepperContainer}>
        <div className={styles.stepper}>
            <div className={`${styles.step} ${styles.completed}`}>
                <div className={styles.stepNum}><IoCheckmarkCircle /></div>
                <span>Giỏ Hàng</span>
            </div>
            <div className={`${styles.connector} ${styles.activeConnector}`}></div>
            <div className={`${styles.step} ${styles.active}`}>
                <div className={styles.stepNum}>2</div>
                <span>Thanh Toán</span>
            </div>
            <div className={styles.connector}></div>
            <div className={styles.step}>
                <div className={styles.stepNum}>3</div>
                <span>Hoàn Tất</span>
            </div>
        </div>
      </div>

      <div className={styles.checkoutContainer}>
        <div className={styles.checkoutLayout}>
          
          {/* --- LEFT: FORM --- */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
                <h2><IoPersonOutline /> Thông Tin Giao Hàng</h2>
            </div>
            <form id="checkout-form" onSubmit={handlePlaceOrderClick}>
              <div className={styles.formGroup}>
                <label>Họ và Tên <span className={styles.required}>*</span></label>
                <div className={styles.inputWrapper}>
                    <IoPersonOutline className={styles.inputIcon} />
                    <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} required placeholder="Ví dụ: Nguyễn Văn A" />
                </div>
              </div>
              
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Email</label>
                  <div className={styles.inputWrapper}>
                    <IoMailOutline className={styles.inputIcon} />
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="email@example.com" />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Số Điện Thoại <span className={styles.required}>*</span></label>
                  <div className={styles.inputWrapper}>
                    <IoCallOutline className={styles.inputIcon} />
                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="09xxxxxxx" />
                  </div>
                </div>
              </div>

              {/* ĐỊA CHỈ (3 CẤP) */}
              <div className={styles.addressSection}>
                <label className={styles.sectionLabel}><IoLocationOutline /> Địa Chỉ Nhận Hàng</label>
                
                <div className={styles.addressGrid}> 
                  {/* 1. Tỉnh/Thành */}
                  <div className={styles.selectWrapper}>
                    <select className={styles.customSelect} value={selectedProvinceId} onChange={handleProvinceChange} required>
                      <option value="">Tỉnh / Thành phố</option>
                      {provinces.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                    </select>
                  </div>

                  {/* 2. Quận/Huyện */}
                  <div className={styles.selectWrapper}>
                    <select className={styles.customSelect} value={selectedDistrictId} onChange={handleDistrictChange} disabled={!selectedProvinceId} required>
                      <option value="">Quận / Huyện</option>
                      {districts.map(d => <option key={d.id} value={d.id}>{d.full_name}</option>)}
                    </select>
                  </div>

                  {/* 3. Phường/Xã (Mới) */}
                  <div className={styles.selectWrapper}>
                    <select className={styles.customSelect} value={selectedWardId} onChange={handleWardChange} disabled={!selectedDistrictId} required>
                      <option value="">Phường / Xã</option>
                      {wards.map(w => <option key={w.id} value={w.id}>{w.full_name}</option>)}
                    </select>
                  </div>
                </div>
                
                <div className={styles.addressInputGroup}>
                  <input type="text" className={styles.fullWidthInput} value={addressParts.specific} onChange={handleSpecificAddress} placeholder="Số nhà, ngõ, tên đường..." required />
                </div>
              </div>

              <div className={styles.formGroup} style={{marginTop: '25px'}}>
                <label><IoDocumentTextOutline style={{verticalAlign:'middle'}}/> Ghi Chú (Tùy chọn)</label>
                <textarea name="notes" value={formData.notes} onChange={handleInputChange} placeholder="Ghi chú về thời gian giao hàng, chỉ dẫn..."></textarea>
              </div>

              {/* PAYMENT METHODS */}
              <div className={styles.paymentSection}>
                <h2><IoWalletOutline /> Phương Thức Thanh Toán</h2>
                <div className={styles.paymentOptions}>
                  <label className={`${styles.paymentOption} ${paymentMethod === 'cod' ? styles.active : ''}`}>
                    <input type="radio" name="paymentMethod" value="cod" checked={paymentMethod === 'cod'} onChange={() => setPaymentMethod('cod')} />
                    <div className={styles.optionContent}>
                        <span className={styles.optionTitle}>Thanh toán khi nhận hàng (COD)</span>
                        <span className={styles.optionDesc}>Kiểm tra hàng rồi thanh toán</span>
                    </div>
                    {paymentMethod === 'cod' && <IoCheckmarkCircle className={styles.checkIcon} />}
                  </label>

                  <label className={`${styles.paymentOption} ${paymentMethod === 'bank' ? styles.active : ''}`}>
                    <input type="radio" name="paymentMethod" value="bank" checked={paymentMethod === 'bank'} onChange={() => setPaymentMethod('bank')} />
                    <div className={styles.optionContent}>
                        <span className={styles.optionTitle}>Chuyển khoản ngân hàng (QR Code)</span>
                        <span className={styles.optionDesc}>Quét mã QR tiện lợi, nhanh chóng</span>
                    </div>
                    {paymentMethod === 'bank' && <IoCheckmarkCircle className={styles.checkIcon} />}
                  </label>
                </div>
              </div>
            </form>
          </div>

          {/* --- RIGHT: SUMMARY --- */}
          <div className={styles.summarySection}>
            <div className={styles.summaryCard}>
              <h3>Đơn Hàng ({cartItems.length})</h3>
              <div className={styles.itemList}>
                {cartItems.map(item => {
                  const originalPrice = parseFloat(item.price || 0);
                  const salePrice = parseFloat(item.sale_price || 0);
                  const isDiscounted = salePrice > 0 && salePrice < originalPrice;
                  const finalItemPrice = isDiscounted ? salePrice : originalPrice;

                  return (
                    <div key={item.id} className={styles.summaryItem}>
                      <div className={styles.itemImgWrapper}>
                        <div className={styles.imgInner}>
                          <img 
                            src={item.thumbnail_url ? `${BACKEND_URL}${item.thumbnail_url}` : PLACEHOLDER_IMAGE} 
                            alt={item.name} 
                          />
                        </div>
                        <span className={styles.itemQty}>{item.quantity}</span>
                      </div>

                      <div className={styles.itemInfo}>
                        <div className={styles.infoRow}>
                          <span className={styles.itemName}>{item.name}</span>
                          <span className={styles.itemFinalPrice}>
                              {formatPrice(finalItemPrice * item.quantity)}
                          </span>
                        </div>
                        
                        <div className={styles.metaRow}>
                          <span className={styles.itemVariant}>Tiêu chuẩn</span>
                          {isDiscounted && (
                              <span className={styles.itemOriginalPrice}>
                                {formatPrice(originalPrice * item.quantity)}
                              </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* DISCOUNT */}
              <div className={styles.discountSection}>
                <div className={styles.discountInputGroup}>
                  <IoTicketOutline className={styles.ticketIcon} />
                  <input 
                    type="text" 
                    placeholder={hasDiscountedItems ? "Không áp dụng cho SP giảm giá" : "Mã giảm giá"} 
                    value={discountCode} 
                    onChange={(e) => setDiscountCode(e.target.value)} 
                    disabled={!!appliedCoupon || hasDiscountedItems} 
                    className={hasDiscountedItems ? styles.disabledInput : ''}
                  />
                  {!appliedCoupon ? (
                    <button 
                        type="button" 
                        onClick={handleApplyCoupon} 
                        className={styles.applyBtn} 
                        disabled={hasDiscountedItems || !discountCode}
                    >
                        Áp dụng
                    </button>
                  ) : (
                    <button type="button" onClick={removeCoupon} className={styles.removeBtn}>Gỡ</button>
                  )}
                </div>
                
                {hasDiscountedItems && (
                    <div className={styles.warningMsg}>
                        <IoBan /> Đơn có sản phẩm đang sale nên không áp dụng thêm Voucher.
                    </div>
                )}

                {appliedCoupon && (
                    <div className={styles.couponSuccessMsg}>
                        <IoCheckmarkCircle /> Đã dùng: <strong>{appliedCoupon.code}</strong> 
                    </div>
                )}
              </div>

              <div className={styles.divider}></div>
              
              <div className={styles.costRow}>
                  <span>Tạm tính</span>
                  <span>{formatPrice(cartTotal)}</span>
              </div>
              <div className={styles.costRow}>
                  <span>Phí vận chuyển</span>
                  {shippingFee > 0 ? 
                    <span>{formatPrice(shippingFee)}</span> : 
                    <span className={styles.waitingText}>{!selectedProvinceId ? 'Chưa tính' : 'Miễn phí'}</span>
                  }
              </div>
              
              {discountAmount > 0 && (
                <div className={`${styles.costRow} ${styles.discountRow}`}>
                    <span>Giảm giá</span>
                    <span>- {formatPrice(discountAmount)}</span>
                </div>
              )}
              
              <div className={styles.divider}></div>
              <div className={`${styles.costRow} ${styles.totalRow}`}>
                <span>Tổng thanh toán</span>
                <span className={styles.totalPrice}>{formatPrice(finalTotal)}</span>
              </div>

              <button type="submit" form="checkout-form" className={styles.placeOrderBtn}>
                {paymentMethod === 'bank' ? 'TIẾP TỤC THANH TOÁN' : 'ĐẶT HÀNG NGAY'}
              </button>
              
              <Link to="/gio-hang" className={styles.backToCartLink}>
                <IoArrowBack /> Quay lại giỏ hàng
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* --- PAYMENT MODAL --- */}
      {showPaymentModal && tempOrderData && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
                <h3>Thanh Toán Chuyển Khoản</h3>
                <button onClick={() => setShowPaymentModal(false)} className={styles.closeModalBtn}><IoCloseOutline /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.orderInfoSummary}>
                  <p>Mã đơn hàng: <strong>{tempOrderData.orderCode}</strong></p>
                  <p>Số tiền: <strong className={styles.highlightPrice}>{formatPrice(tempOrderData.finalTotal)}</strong></p>
              </div>
              
              <div className={styles.qrContainer}>
                  <img src={qrCodeImage} alt="QR Code" className={styles.qrImage} />
                  <p className={styles.scanText}>Quét mã bằng App ngân hàng</p>
              </div>
              
              <div className={styles.transferInfoBox}>
                  <label>Nội dung chuyển khoản (Bắt buộc):</label>
                  <div className={styles.copyRow}>
                      <input type="text" value={tempOrderData.transferContent} readOnly />
                      <button type="button" onClick={handleCopyContent} className={styles.copyBtn}>
                          <IoCopyOutline /> COPY
                      </button>
                  </div>
                  <small><IoWarningOutline /> Vui lòng nhập chính xác nội dung này.</small>
              </div>
              
              <div className={styles.uploadSection}>
                  <label>Tải ảnh minh chứng chuyển khoản:</label>
                  <div className={styles.fileUploadWrapper}>
                      <label htmlFor="modalProofFile" className={styles.uploadBtn}>
                          <IoCloudUploadOutline /> {proofFile ? 'Đổi ảnh khác' : 'Chọn ảnh'}
                      </label>
                      <input type="file" id="modalProofFile" hidden onChange={handleFileChange} accept="image/*" />
                  </div>
                  {proofFile && <div className={styles.fileName}><IoCheckmarkCircle /> {proofFile.name}</div>}
              </div>
            </div>
            
            <div className={styles.modalFooter}>
                <button onClick={() => setShowPaymentModal(false)} className={styles.cancelBtn}>Quay lại</button>
                <button onClick={() => submitOrderToBackend(tempOrderData)} className={styles.confirmBtn}>
                    HOÀN TẤT ĐẶT HÀNG
                </button>
            </div>
          </div>
        </div>
      )}

      {/* --- SUCCESS POPUP --- */}
      {showSuccessPopup && (
        <div className={styles.popupOverlay}>
          <div className={styles.successBox}>
            <IoCheckmarkCircle className={styles.successIcon} />
            <h3>ĐẶT HÀNG THÀNH CÔNG!</h3>
            <p>Cảm ơn bạn đã mua sắm tại Hương Trầm Cổ Mộc.</p>
            
            <div className={styles.codeBox}>
                <span>Mã đơn hàng của bạn</span>
                <strong>{createdOrderCode}</strong>
            </div>
            
            <p className={styles.subText}>Thông tin chi tiết đã được gửi tới email.<br/>Chúng tôi sẽ sớm liên hệ để xác nhận.</p>
            
            <button onClick={handleCloseSuccessPopup} className={styles.homeBtn}>
                <IoHome style={{marginRight: '5px', marginBottom:'-2px'}}/> VỀ TRANG CHỦ
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;